import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import type { SnapshotResponse } from '../api';

export interface VsmDiagramHandle {
  highlightRegions: (ids: string[]) => void;
  triggerAlgedonicPulse: () => void;
}

interface Props {
  snapshot: SnapshotResponse | null;
}

// Region hotspots — coordinates eyeballed against the 970x1330 scan.
// Each entry produces a transparent <rect>/<ellipse> overlay that highlights
// when its id is added to the active set, and a <title> for hover tooltips.
type Hotspot =
  | { id: string; label: string; kind: 'rect'; x: number; y: number; w: number; h: number; rx?: number }
  | { id: string; label: string; kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number };

const HOTSPOTS: Hotspot[] = [
  { id: 's5-policy', label: 'System 5 — Policy / Homeostasis', kind: 'rect', x: 495, y: 60, w: 240, h: 125, rx: 6 },
  { id: 'monitor', label: 'S5 Monitor (lightbulb)', kind: 'rect', x: 500, y: 125, w: 50, h: 55, rx: 4 },
  { id: 'homeostasis', label: 'S5 Homeostasis (eyes)', kind: 'rect', x: 680, y: 125, w: 50, h: 55, rx: 4 },
  { id: 's4-outside-future', label: 'System 4 — Outside & Future', kind: 'rect', x: 495, y: 225, w: 240, h: 135, rx: 6 },
  { id: 's3-inside-now', label: 'System 3 — Inside & Now', kind: 'rect', x: 495, y: 380, w: 240, h: 125, rx: 6 },
  { id: 's2-anti-oscillatory', label: 'System 2 — Anti-oscillatory', kind: 'rect', x: 760, y: 380, w: 80, h: 115, rx: 6 },
  { id: 's3star-audit', label: 'System 3★ — Sporadic Audit', kind: 'rect', x: 370, y: 540, w: 105, h: 80, rx: 6 },
  { id: 's1-recursion-a', label: 'System 1 — Recursion A', kind: 'rect', x: 520, y: 585, w: 250, h: 185, rx: 6 },
  { id: 's1-recursion-b', label: 'System 1 — Recursion B', kind: 'rect', x: 520, y: 870, w: 250, h: 185, rx: 6 },
  { id: 'environment-total', label: 'Total Environment', kind: 'rect', x: 30, y: 30, w: 230, h: 1150, rx: 60 },
  { id: 'environment-future', label: 'Future Environment', kind: 'ellipse', cx: 155, cy: 180, rx: 105, ry: 100 },
  { id: 'environment-local-a', label: 'Local Environment A', kind: 'ellipse', cx: 350, cy: 770, rx: 115, ry: 105 },
  { id: 'environment-local-b', label: 'Local Environment B', kind: 'ellipse', cx: 350, cy: 1050, rx: 115, ry: 105 },
];

const ALGEDONIC_PATH = 'M 645 1000 L 645 770 L 540 620 L 540 200';

export const VsmDiagram = forwardRef<VsmDiagramHandle, Props>(function VsmDiagram(
  { snapshot },
  ref
) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [active, setActive] = useState<Set<string>>(new Set());

  useImperativeHandle(ref, () => ({
    highlightRegions(ids) {
      setActive(new Set(ids));
    },
    triggerAlgedonicPulse() {
      const pulse = svgRef.current?.querySelector('#algedonic-pulse');
      if (!pulse) return;
      pulse.classList.remove('active');
      void (pulse as SVGElement).getBoundingClientRect();
      pulse.classList.add('active');
    },
  }));

  const tooltipFor = (h: Hotspot): string => {
    if (!snapshot) return h.label;
    const daemonName = mapDaemon(h.id);
    if (daemonName) {
      const d = snapshot.daemons.find(x => x.name === daemonName);
      if (d) {
        const tick =
          d.last_tick_age_ms != null
            ? `${Math.round(d.last_tick_age_ms / 1000)}s since last tick`
            : 'no tick reported';
        return `${h.label}\n${d.healthy ? '● online' : '○ offline'} ${d.name} :${d.port}\n${tick}`;
      }
    }
    if (h.id === 'algedonic-channel') {
      return `${h.label}\n${snapshot.recent_algedonic.length} recent algedonic event(s)`;
    }
    return h.label;
  };

  return (
    <svg
      ref={svgRef}
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 970 1330'
      preserveAspectRatio='xMidYMid meet'
      role='img'
      aria-label='Stafford Beer Figure 37 - The Viable System Model'
    >
      <image href='/vsm-figure-37.png' x={0} y={0} width={970} height={1330} />

      {HOTSPOTS.map(h => (
        <g
          key={h.id}
          id={h.id}
          data-label={h.label}
          className={`region${active.has(h.id) ? ' highlight' : ''}`}
        >
          {h.kind === 'rect' ? (
            <rect x={h.x} y={h.y} width={h.w} height={h.h} rx={h.rx ?? 0} />
          ) : (
            <ellipse cx={h.cx} cy={h.cy} rx={h.rx} ry={h.ry} />
          )}
          <title>{tooltipFor(h)}</title>
        </g>
      ))}

      {/* Algedonic channel: hotspot + animated pulse stroke (the pulse animates via CSS) */}
      <g
        id='algedonic-channel'
        data-label='Algedonic Channel'
        className={`region${active.has('algedonic-channel') ? ' highlight' : ''}`}
      >
        <path d={ALGEDONIC_PATH} fill='none' />
        <title>{tooltipFor({ id: 'algedonic-channel', label: 'Algedonic Channel' } as Hotspot)}</title>
      </g>
      <path id='algedonic-pulse' className='algedonic-pulse' d={ALGEDONIC_PATH} pathLength={100} />
    </svg>
  );
});

/**
 * Map the SVG region id to the substrate daemon name that owns it.
 * Keep in sync with SnapshotAggregator.ts on the backend.
 */
function mapDaemon(regionId: string): string | null {
  switch (regionId) {
    case 's5-policy':
    case 'monitor':
    case 'homeostasis':
      return 'vasm-s5-policy-resident';
    case 's3-inside-now':
      return 'vasm-s3-control';
    case 's3star-audit':
      return 'vasm-s3star-auditor';
    case 's2-anti-oscillatory':
      return 'vasm-s2-coordinator';
    case 's1-recursion-a':
    case 's1-recursion-b':
      return 'hermes-gateway-s1-s2';
    default:
      return null;
  }
}
